import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useAppSelector } from '../../store/redux/hooks'
import {
  StyledPageHeaderContainer,
  StyledPageHeaderSectionBetween,
  StyledPageHeaderSectionLeft,
  StyledPageHeaderSectionRight,
} from '../../styles/PageHeaderStyles'
import { getButtonsSettings } from '../../store/redux/reducers/pageHeaderSlice'
import { useSelector } from 'react-redux'
import InfoButton from '../Info/InfoButton'
import MonthPicker from '../MonthPicker/ReduxMonthPicker'
import ProjectDistributionHeaderComponent from '../Header/ProjectDistributionHeaderComponent'
import ProjectDistributionLeaderSelectComponent from '../Header/ProjectDistributionLeaderSelectComponent'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import BreadcrumbsContainer from '../Breadcrumbs/BreadcrumbsContainer'
import { StyledContainerColumn, StyledContainerRowBetween, StyledContainerRowStart } from '../../styles/ContainerStyles'
import YearPicker from '../YearPicker/YearPicker'
import PageHeaderAvatar from './PageHeaderAvatar'
import useOverflowCheck from '../../store/utils/useOverflowCheck'
import NotificationSidebar from '../Sidebar/notifications/NotificationSidebar/NotificationSidebar'

const PageHeader = () => {
  const theme = useTheme()
  const pageTitleRef = useRef<HTMLDivElement>(null)
  const displayTooltip = useOverflowCheck(pageTitleRef)
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const pageName = useAppSelector(state => state.pageName.value)
  const buttonsSettings = useSelector(getButtonsSettings)
  const currentUser = useSelector(getCurrentUser)

  const { profileIcon, profileExtension } = useMemo(() => {
    if (currentUser && currentUser.avatar && currentUser.extension) {
      return { profileIcon: currentUser.avatar, profileExtension: currentUser.extension }
    }
    return { profileIcon: undefined, profileExtension: undefined }
  }, [currentUser])

  const [somethingAddedToHeader, setSomethingAddedToHeader] = useState<boolean>(false)

  useEffect(() => {
    if (
      buttonsSettings.monthPicker ||
      buttonsSettings.yearPicker ||
      buttonsSettings.leaderPicker?.shown ||
      buttonsSettings.monthRangePicker?.shown
    ) {
      setSomethingAddedToHeader(true)
    } else {
      setSomethingAddedToHeader(false)
    }
  }, [buttonsSettings])

  return (
    <StyledContainerColumn
      sx={{
        minHeight: isDownSm && somethingAddedToHeader ? 'auto !important' : '75px',
        height: isDownSm && somethingAddedToHeader ? 'auto !important' : '75px',
      }}
    >
      <StyledPageHeaderContainer sx={{ px: { xs: 0, sm: 1, md: 2 }, gap: { md: 2 } }}>
        <StyledPageHeaderSectionLeft
          sx={{
            width: { xs: '100%', lg: '20%' },
            gap: { xs: 0, sm: 1, md: 2 },
            maxWidth: { xs: '70%' },
          }}
        >
          <Tooltip title={displayTooltip ? pageName : ''} placement="bottom">
            <Typography
              ref={pageTitleRef}
              component="h1"
              variant="h4"
              sx={{
                color: 'text.highEmphasis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {pageName}
            </Typography>
          </Tooltip>
          {buttonsSettings.settingsButton && <p>Settings</p>}
          {buttonsSettings.infoButton && <InfoButton />}
        </StyledPageHeaderSectionLeft>
        {!isDownSm && (
          <StyledPageHeaderSectionBetween sx={{ gap: 5 }}>
            {buttonsSettings.monthPicker && <MonthPicker />}
            {buttonsSettings.yearPicker && <YearPicker />}
            {buttonsSettings.leaderPicker?.shown && <ProjectDistributionLeaderSelectComponent />}
            {buttonsSettings.monthRangePicker?.shown && <ProjectDistributionHeaderComponent />}
          </StyledPageHeaderSectionBetween>
        )}
        <StyledPageHeaderSectionRight
          sx={{ width: { xs: '25%', md: 'auto' }, minWidth: '20%', gap: { xs: 1, md: 1.5 } }}
        >
          <NotificationSidebar />
          <Typography
            component="span"
            variant="body1"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              color: 'text.mediumEmphasis',
              textOverflow: 'ellipsis',
              pt: 0.5,
              display: { xs: 'none', md: 'block' },
            }}
          >
            {`${currentUser?.firstName} ${currentUser?.lastName}`}
          </Typography>
          <PageHeaderAvatar
            profileIcon={profileIcon}
            profileExtension={profileExtension}
            displayTooltip={displayTooltip}
            currentUser={currentUser}
          />
        </StyledPageHeaderSectionRight>
      </StyledPageHeaderContainer>
      {isDownSm && (
        <StyledContainerRowBetween sx={{ justifyContent: 'center', flexDirection: 'column', pb: { xs: 1 } }}>
          {buttonsSettings.monthPicker && <MonthPicker />}
          {buttonsSettings.yearPicker && <YearPicker />}
          {buttonsSettings.leaderPicker?.shown && <ProjectDistributionLeaderSelectComponent />}
          {buttonsSettings.monthRangePicker?.shown && <ProjectDistributionHeaderComponent />}
        </StyledContainerRowBetween>
      )}
      <StyledContainerRowStart sx={{ px: { xs: 0, sm: 1, md: 2 }, mt: { md: '-4px', lg: '-8px' } }}>
        <BreadcrumbsContainer />
      </StyledContainerRowStart>
    </StyledContainerColumn>
  )
}

export default PageHeader
